'use client'

import { useEffect, useState, useRef } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { TechStackSection } from '@/components/tech-stack-section'
import { MarketOutlookSection } from '@/components/market-outlook-section'
import { ProcessFlow } from '@/components/process-flow'
import { RotatingGlobeSection } from '@/components/rotating-globe-section'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowRight, Info, Play, ChevronDown, Shield, TrendingUp, Users, Zap, Target, Brain, BarChart3, FileText, Clock, DollarSign, Search, Workflow, Bot, GraduationCap, Landmark, Building2 } from 'lucide-react'
import { FloatingChatButton } from '@/components/floating-chat-button'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'

export default function Home() {
  const [isLoading, setIsLoading] = useState(true)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const heroRef = useRef<HTMLElement>(null)
  const isMobile = useIsMobile()

  useEffect(() => {
    window.scrollTo(0, 0)
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual'
    }
    const timer = setTimeout(() => setIsLoading(false), isMobile ? 800 : 1500)
    return () => {
      clearTimeout(timer)
    }
  }, [isMobile])

  return (
    <>
      <AnimatePresence>
        {isLoading && (
          <motion.div
            key="loader"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-background"
          >
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </motion.div>
        )}
      </AnimatePresence>
      {!isLoading && (
        <motion.div
          key="content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="relative min-h-screen overflow-hidden"
        >
          {/* Background Layer - Simplified on mobile for performance */}
          <div className="fixed inset-0 -z-10 pointer-events-none overflow-hidden">
            {isMobile ? (
              /* Mobile: Simple static gradient background - no animations */
              <div className="absolute inset-0">
                {/* Static gradient background */}
                <div 
                  className="absolute inset-0 opacity-0 dark:opacity-100"
                  style={{
                    background: `
                      linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
                      linear-gradient(225deg, rgba(56, 189, 248, 0.15) 0%, transparent 50%),
                      linear-gradient(315deg, rgba(59, 130, 246, 0.1) 0%, transparent 50%)
                    `,
                  }}
                />
                {/* Simple static grid pattern - no animation */}
                <div 
                  className="absolute inset-0 opacity-[0.08] dark:opacity-[0.12]"
                  style={{
                    backgroundImage: `
                      linear-gradient(to right, rgba(59, 130, 246, 0.3) 1px, transparent 1px),
                      linear-gradient(to bottom, rgba(59, 130, 246, 0.3) 1px, transparent 1px)
                    `,
                    backgroundSize: '50px 50px',
                  }}
                />
              </div>
            ) : (
              /* Desktop: Full animated background */
              <div className="absolute inset-0" style={{ willChange: 'transform' }}>
                {/* Animated Grid Pattern with Rotation */}
                <div 
                  className="absolute inset-0 opacity-[0.15] dark:opacity-[0.2]"
                  style={{
                    backgroundImage: `
                      linear-gradient(to right, rgba(59, 130, 246, 0.4) 1px, transparent 1px),
                      linear-gradient(to bottom, rgba(59, 130, 246, 0.4) 1px, transparent 1px)
                    `,
                    backgroundSize: '50px 50px',
                    animation: 'grid-drift-rotate 20s linear infinite',
                  }}
                />
                
                {/* Secondary Grid Layer */}
                <div 
                  className="absolute inset-0 opacity-[0.15] dark:opacity-[0.15]"
                  style={{
                    backgroundImage: `
                      linear-gradient(to right, rgba(59, 130, 246, 0.35) 1px, transparent 1px),
                      linear-gradient(to bottom, rgba(59, 130, 246, 0.35) 1px, transparent 1px)
                    `,
                    backgroundSize: '80px 80px',
                    animation: 'grid-drift-rotate-reverse 25s linear infinite',
                  }}
                />

                {/* Floating Gradient Orbs */}
                <div className="absolute top-0 left-[10%] w-[600px] h-[600px]">
                  <div className="absolute inset-0 bg-blue-700/[0.04] dark:bg-blue-500/20 rounded-full blur-3xl animate-float-orb-1" />
                  <div className="absolute inset-0 border border-blue-600/[0.04] dark:border-blue-400/20 rounded-full" style={{ animation: 'pulse-ring 4s ease-in-out infinite' }} />
                </div>
                <div className="absolute top-[50%] left-[5%] w-[450px] h-[450px]">
                  <div className="absolute inset-0 bg-blue-800/[0.03] dark:bg-blue-600/18 rounded-full blur-3xl animate-float-orb-3" />
                  <div className="absolute inset-0 border border-blue-700/[0.04] dark:border-blue-500/20 rounded-full" style={{ animation: 'pulse-ring 4.5s ease-in-out infinite', animationDelay: '0.5s' }} />
                </div>
                <div className="absolute bottom-[10%] right-[10%] w-[550px] h-[550px]">
                  <div className="absolute inset-0 bg-blue-700/[0.04] dark:bg-sky-500/20 rounded-full blur-3xl animate-float-orb-4" />
                  <div className="absolute inset-0 border border-blue-600/[0.04] dark:border-sky-400/20 rounded-full" style={{ animation: 'pulse-ring 5.5s ease-in-out infinite', animationDelay: '1.5s' }} />
                </div>
                <div className="absolute top-[70%] left-[50%] w-[400px] h-[400px]">
                  <div className="absolute inset-0 bg-blue-600/[0.03] dark:bg-blue-400/18 rounded-full blur-3xl animate-float-orb-5" />
                  <div className="absolute inset-0 border border-blue-500/[0.04] dark:border-blue-300/20 rounded-full" style={{ animation: 'pulse-ring 4.2s ease-in-out infinite', animationDelay: '0.8s' }} />
                </div>
                <div className="absolute bottom-[30%] left-[30%] w-[480px] h-[480px]">
                  <div className="absolute inset-0 bg-blue-800/[0.03] dark:bg-sky-600/18 rounded-full blur-3xl animate-float-orb-6" />
                  <div className="absolute inset-0 border border-blue-700/[0.04] dark:border-sky-500/20 rounded-full" style={{ animation: 'pulse-ring 4.8s ease-in-out infinite', animationDelay: '1.2s' }} />
                </div>

                {/* Animated Flow Lines */}
                <svg className="absolute inset-0 w-full h-full opacity-20 dark:opacity-15" style={{ zIndex: 1 }}>
                  <defs>
                    <linearGradient id="flowGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="rgba(59, 130, 246, 0.3)" stopOpacity="0" />
                      <stop offset="50%" stopColor="rgba(59, 130, 246, 0.5)" stopOpacity="1" />
                      <stop offset="100%" stopColor="rgba(59, 130, 246, 0.3)" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="flowGradient2" x1="100%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="rgba(56, 189, 248, 0.3)" stopOpacity="0" />
                      <stop offset="50%" stopColor="rgba(56, 189, 248, 0.5)" stopOpacity="1" />
                      <stop offset="100%" stopColor="rgba(56, 189, 248, 0.3)" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M0,200 Q200,100 400,200 T800,200"
                    stroke="url(#flowGradient1)"
                    strokeWidth="2"
                    fill="none"
                    style={{ filter: 'blur(1px)', animation: 'flow-line-1 8s ease-in-out infinite' }}
                  />
                  <path
                    d="M0,400 Q300,300 600,400 T1200,400"
                    stroke="url(#flowGradient2)"
                    strokeWidth="2"
                    fill="none"
                    style={{ filter: 'blur(1px)', animation: 'flow-line-2 10s ease-in-out infinite', animationDelay: '2s' }}
                  />
                  <path
                    d="M0,600 Q250,500 500,600 T1000,600"
                    stroke="url(#flowGradient1)"
                    strokeWidth="2"
                    fill="none"
                    style={{ filter: 'blur(1px)', animation: 'flow-line-3 9s ease-in-out infinite', animationDelay: '4s' }}
                  />
                </svg>

                {/* Particle Network */}
                <div className="absolute inset-0" style={{ zIndex: 2 }}>
                  {[
                    { size: 3, left: 15, top: 20, duration: 18, delay: 0, id: 'p1' },
                    { size: 2.5, left: 85, top: 10, duration: 22, delay: 1, id: 'p2' },
                    { size: 4, left: 45, top: 35, duration: 16, delay: 2, id: 'p3' },
                    { size: 3.5, left: 70, top: 60, duration: 20, delay: 0.5, id: 'p4' },
                    { size: 2, left: 25, top: 75, duration: 24, delay: 1.5, id: 'p5' },
                    { size: 3, left: 90, top: 40, duration: 19, delay: 2.5, id: 'p6' },
                    { size: 2.5, left: 10, top: 50, duration: 21, delay: 3, id: 'p7' },
                    { size: 4, left: 60, top: 15, duration: 17, delay: 0.8, id: 'p8' },
                    { size: 3, left: 35, top: 85, duration: 23, delay: 1.2, id: 'p9' },
                    { size: 2.5, left: 80, top: 25, duration: 18, delay: 2.2, id: 'p10' },
                    { size: 3.5, left: 5, top: 30, duration: 20, delay: 0.3, id: 'p11' },
                    { size: 2, left: 55, top: 70, duration: 25, delay: 1.8, id: 'p12' },
                    { size: 4, left: 95, top: 55, duration: 16, delay: 2.8, id: 'p13' },
                    { size: 3, left: 20, top: 5, duration: 22, delay: 0.6, id: 'p14' },
                    { size: 2.5, left: 65, top: 45, duration: 19, delay: 1.4, id: 'p15' },
                  ].map((particle, i) => (
                    <div key={particle.id}>
                      {i < 8 && (
                        <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 1 }}>
                          <line
                            x1={`${particle.left}%`}
                            y1={`${particle.top}%`}
                            x2={`${[
                              { left: 15, top: 20 }, { left: 85, top: 10 }, { left: 45, top: 35 },
                              { left: 70, top: 60 }, { left: 25, top: 75 }, { left: 90, top: 40 },
                              { left: 10, top: 50 }, { left: 60, top: 15 },
                            ][(i + 1) % 8].left}%`}
                            y2={`${[
                              { left: 15, top: 20 }, { left: 85, top: 10 }, { left: 45, top: 35 },
                              { left: 70, top: 60 }, { left: 25, top: 75 }, { left: 90, top: 40 },
                              { left: 10, top: 50 }, { left: 60, top: 15 },
                            ][(i + 1) % 8].top}%`}
                            stroke="rgba(59, 130, 246, 0.15)"
                            strokeWidth="0.5"
                            style={{ 
                              filter: 'blur(0.5px)',
                              animation: `pulse-line ${3 + i * 0.5}s ease-in-out infinite`,
                              animationDelay: `${i * 0.3}s`
                            }}
                          />
                        </svg>
                      )}
                      <div
                        className="absolute rounded-full bg-blue-600/10 dark:bg-blue-300/30"
                        style={{
                          width: `${particle.size}px`,
                          height: `${particle.size}px`,
                          left: `${particle.left}%`,
                          top: `${particle.top}%`,
                          animation: `particle-float ${particle.duration}s ease-in-out infinite`,
                          animationDelay: `${particle.delay}s`,
                          boxShadow: '0 0 4px rgba(59, 130, 246, 0.3)',
                        }}
                      />
                      <div
                        className="absolute rounded-full bg-blue-600/5 dark:bg-blue-300/15"
                        style={{
                          width: `${particle.size * 2}px`,
                          height: `${particle.size * 2}px`,
                          left: `${particle.left}%`,
                          top: `${particle.top}%`,
                          transform: 'translate(-50%, -50%)',
                          animation: `particle-float ${particle.duration}s ease-in-out infinite`,
                          animationDelay: `${particle.delay}s`,
                          filter: 'blur(3px)',
                        }}
                      />
                    </div>
                  ))}
                </div>

                {/* Flowing Gradient Overlay */}
                <div 
                  className="absolute inset-0 opacity-10 dark:opacity-40"
                  style={{
                    background: `
                      radial-gradient(circle at 20% 30%, rgba(59, 130, 246, 0.25) 0%, transparent 50%),
                      radial-gradient(circle at 80% 70%, rgba(56, 189, 248, 0.25) 0%, transparent 50%)
                    `,
                    backgroundSize: '200% 200%',
                    animation: 'gradient-flow 20s ease-in-out infinite',
                  }}
                />
              </div>
            )}
          </div>
          {/* Hero Section - Minimalist Transparent */}
          <section 
            ref={heroRef}
            className="relative min-h-screen flex items-center justify-center overflow-hidden z-10"
          >
            <div className="container mx-auto px-4 py-16 relative z-10">
              <div className="text-center max-w-4xl mx-auto space-y-5 relative">
                <div 
                  className={cn(
                    "relative rounded-3xl p-6 md:py-12 md:px-14 border",
                    isMobile 
                      ? "bg-slate-900/95 border-blue-500/30" 
                      : "backdrop-blur-xl border-blue-200/50 dark:border-white/10 dark:bg-black/10 hero-card-light"
                  )}
                  style={isMobile ? {
                    boxShadow: '0 4px 20px rgba(59, 130, 246, 0.15)',
                  } : {
                    boxShadow: `
                      0 0 20px rgba(59, 130, 246, 0.2),
                      0 0 60px rgba(59, 130, 246, 0.15),
                      0 0 120px rgba(59, 130, 246, 0.08),
                      0 8px 32px rgba(0, 0, 0, 0.06)
                    `,
                  }}
                >
                  {!isMobile && (
                    <div 
                      className="absolute inset-0 rounded-3xl border-2 border-blue-400/30 dark:border-blue-500/20"
                      style={{
                        animation: 'hero-glow-pulse 3s ease-in-out infinite',
                        boxShadow: '0 0 40px rgba(59, 130, 246, 0.2)',
                      }}
                    />
                  )}
                  
                  <div className="relative z-10 flex flex-col items-center gap-5 md:gap-6">
                    <AnimatePresence mode="wait">
                      {isVideoPlaying ? (
                        <motion.div
                          key="video"
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ duration: 0.4, ease: "easeOut" }}
                          className="w-full flex flex-col items-center gap-4"
                        >
                          <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl shadow-blue-500/20" style={{ aspectRatio: '16/9' }}>
                            <video
                              ref={videoRef}
                              src="/GovConData_Marketing_Video.mp4"
                              className="w-full h-full object-cover rounded-2xl"
                              controls
                              playsInline
                              onEnded={() => {
                                setIsVideoPlaying(false)
                                if (videoRef.current) {
                                  videoRef.current.pause()
                                  videoRef.current.currentTime = 0
                                }
                              }}
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-sm text-muted-foreground hover:text-foreground gap-2"
                            onClick={() => {
                              setIsVideoPlaying(false)
                              if (videoRef.current) {
                                videoRef.current.pause()
                                videoRef.current.currentTime = 0
                              }
                            }}
                          >
                            <ArrowRight className="h-3.5 w-3.5 rotate-180" />
                            Back
                          </Button>
                        </motion.div>
                      ) : (
                        <motion.div
                          key="content"
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ duration: 0.4, ease: "easeOut" }}
                          className="flex flex-col items-center gap-5 md:gap-6 w-full"
                        >
                    <motion.h1 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                      className="text-4xl sm:text-6xl md:text-7xl font-bold tracking-tight"
                    >
                      <span 
                        className="inline-block bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-cyan-500 to-sky-500" 
                        style={{
                          WebkitTextFillColor: 'transparent',
                          WebkitBackgroundClip: 'text',
                          backgroundClip: 'text',
                          backgroundSize: '200% 100%',
                          backgroundPosition: '0% 50%',
                        }}
                      >
                        Built to Win
                      </span>
                      <br />
                      <span className="text-gray-900 dark:text-white font-bold">Government Contracts</span>
                    </motion.h1>

                    <motion.p 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                      className="text-base sm:text-lg md:text-xl text-gray-700 dark:text-white max-w-2xl mx-auto font-medium"
                    >
                      The #1 AI platform for government contractors—aggregating millions of opportunities, automating proposals, and delivering the intelligence you need to win more contracts.
                    </motion.p>

                    <motion.div 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
                      className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center w-full sm:w-auto"
                    >
                      <div className="relative group/glow rounded-lg p-px overflow-hidden">
                        <div className="absolute inset-0 rounded-lg animate-border-glow" style={{ background: 'conic-gradient(from 0deg, transparent 0%, transparent 60%, rgba(56, 189, 248, 0.8) 75%, rgba(59, 130, 246, 0.9) 85%, rgba(99, 102, 241, 0.8) 95%, transparent 100%)' }} />
                        <Button 
                          size="lg" 
                          variant="outline" 
                          className="relative text-base sm:text-lg px-6 sm:px-8 h-11 sm:h-12 backdrop-blur-sm bg-background border-0 text-foreground hover:bg-gray-100 dark:hover:bg-white/5 shadow-md w-full inline-flex items-center gap-2"
                          onClick={() => {
                            setIsVideoPlaying(true)
                            setTimeout(() => {
                              videoRef.current?.play()
                            }, 400)
                          }}
                        >
                          <Play className="h-4 w-4" />
                          See It In Action
                        </Button>
                      </div>
                      <Button asChild size="lg" variant="glow" className="text-base sm:text-lg px-6 sm:px-8 h-11 sm:h-12 relative overflow-hidden group bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 shadow-lg shadow-blue-500/30">
                        <Link href="/pricing">
                          Start Winning Contracts
                          <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </Button>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
                      className="w-full pt-2 md:pt-3 mt-auto border-t border-gray-200/30 dark:border-white/[0.06]"
                    >
                      <div className="flex items-center justify-center gap-3 pt-3 md:pt-4 mb-3">
                        <div className="h-px flex-1 max-w-[60px] bg-gradient-to-r from-transparent to-gray-300 dark:to-white/15" />
                        <p className="text-[10px] sm:text-xs font-semibold tracking-[0.2em] uppercase text-sky-500/70 dark:text-sky-400/50">
                          Trusted by GovCon Teams Nationwide
                        </p>
                        <div className="h-px flex-1 max-w-[60px] bg-gradient-to-l from-transparent to-gray-300 dark:to-white/15" />
                      </div>
                      <div className="relative overflow-hidden rounded-xl">
                        <div className="absolute left-0 top-0 bottom-0 w-12 z-10 bg-gradient-to-r from-white/80 dark:from-black/40 to-transparent pointer-events-none" />
                        <div className="absolute right-0 top-0 bottom-0 w-12 z-10 bg-gradient-to-l from-white/80 dark:from-black/40 to-transparent pointer-events-none" />
                        <div className="flex animate-scroll-badges">
                          {[
                            { icon: Landmark, label: 'Federal Bids' },
                            { icon: Building2, label: 'SLED Bids' },
                            { icon: Bot, label: 'Agentic AI' },
                            { icon: GraduationCap, label: 'GovCon Coaching' },
                            { icon: Zap, label: 'AI-Powered Matching' },
                            { icon: Target, label: 'Smart Bid Scoring' },
                            { icon: Brain, label: 'Intelligent Research' },
                            { icon: Search, label: 'Opportunity Discovery' },
                            { icon: BarChart3, label: 'Pipeline Analytics' },
                            { icon: FileText, label: 'Proposal Assistance' },
                            { icon: Shield, label: 'Secure & Compliant' },
                            { icon: TrendingUp, label: 'Higher Win Rates' },
                            { icon: Users, label: 'Teams of All Sizes' },
                            { icon: Clock, label: 'Real-Time Alerts' },
                            { icon: DollarSign, label: 'Cost Savings' },
                            { icon: Workflow, label: 'Workflow Automation' },
                          ].flatMap((item, _, arr) => [item, ...arr.slice(0, 0)]).concat(
                            [
                              { icon: Landmark, label: 'Federal Bids' },
                              { icon: Building2, label: 'SLED Bids' },
                              { icon: Bot, label: 'Agentic AI' },
                              { icon: GraduationCap, label: 'GovCon Coaching' },
                              { icon: Zap, label: 'AI-Powered Matching' },
                              { icon: Target, label: 'Smart Bid Scoring' },
                              { icon: Brain, label: 'Intelligent Research' },
                              { icon: Search, label: 'Opportunity Discovery' },
                              { icon: BarChart3, label: 'Pipeline Analytics' },
                              { icon: FileText, label: 'Proposal Assistance' },
                              { icon: Shield, label: 'Secure & Compliant' },
                              { icon: TrendingUp, label: 'Higher Win Rates' },
                              { icon: Users, label: 'Teams of All Sizes' },
                              { icon: Clock, label: 'Real-Time Alerts' },
                              { icon: DollarSign, label: 'Cost Savings' },
                              { icon: Workflow, label: 'Workflow Automation' },
                            ]
                          ).map(({ icon: Icon, label }, idx) => (
                            <div
                              key={`${label}-${idx}`}
                              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 mx-1 rounded-full border border-blue-300/50 dark:border-blue-500/30 bg-white/80 dark:bg-blue-500/[0.06] backdrop-blur-sm"
                              style={{ boxShadow: '0 0 8px rgba(59, 130, 246, 0.15), 0 0 20px rgba(59, 130, 246, 0.08)' }}
                            >
                              <Icon className="h-3 w-3 text-blue-500 dark:text-sky-400" />
                              <span className="text-[10px] sm:text-xs font-medium text-gray-900 dark:text-white whitespace-nowrap">{label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>

              {/* Scroll Anchor */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.2 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2"
              >
                <motion.div
                  animate={{ y: [0, 8, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <ChevronDown className="h-6 w-6 text-gray-400 dark:text-white/30" />
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* Main Content */}
          <div className="relative container mx-auto px-4 py-16 z-10">
            <RotatingGlobeSection />
            <ProcessFlow />
            <div 
              className={cn(
                "mt-16 md:mt-32 rounded-3xl p-6 md:p-8 border relative",
                isMobile 
                  ? "bg-slate-900/95 border-blue-500/30" 
                  : "backdrop-blur-xl border-white/20 dark:border-white/10 bg-white/5 dark:bg-black/10"
              )}
              style={isMobile ? {
                boxShadow: '0 4px 20px rgba(59, 130, 246, 0.15)',
              } : {
                boxShadow: `
                  0 8px 32px 0 rgba(31, 38, 135, 0.1),
                  0 0 0 1px rgba(255, 255, 255, 0.1) inset,
                  0 0 60px rgba(59, 130, 246, 0.1)
                `,
              }}
            >
              {!isMobile && (
                <div 
                  className="absolute inset-0 rounded-3xl border-2 border-blue-400/30 dark:border-blue-500/20"
                  style={{
                    animation: 'hero-glow-pulse 3s ease-in-out infinite',
                    boxShadow: '0 0 40px rgba(59, 130, 246, 0.2)',
                  }}
                />
              )}
              <div className="relative z-10">
                <TechStackSection isMobile={isMobile} />
              </div>
            </div>
            <div 
              className={cn(
                "mt-16 md:mt-32 rounded-3xl p-6 md:p-8 border relative",
                isMobile 
                  ? "bg-slate-900/95 border-blue-500/30" 
                  : "backdrop-blur-xl border-white/20 dark:border-white/10 bg-white/5 dark:bg-black/10"
              )}
              style={isMobile ? {
                boxShadow: '0 4px 20px rgba(59, 130, 246, 0.15)',
              } : {
                boxShadow: `
                  0 8px 32px 0 rgba(31, 38, 135, 0.1),
                  0 0 0 1px rgba(255, 255, 255, 0.1) inset,
                  0 0 60px rgba(59, 130, 246, 0.1)
                `,
              }}
            >
              {!isMobile && (
                <div 
                  className="absolute inset-0 rounded-3xl border-2 border-blue-400/30 dark:border-blue-500/20"
                  style={{
                    animation: 'hero-glow-pulse 3s ease-in-out infinite',
                    boxShadow: '0 0 40px rgba(59, 130, 246, 0.2)',
                  }}
                />
              )}
              <div className="relative z-10">
                <MarketOutlookSection isMobile={isMobile} />
              </div>
            </div>

            {/* Impact Stats */}
            <motion.section
              initial={{ opacity: 0, y: isMobile ? 20 : 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: isMobile ? 0.3 : 0.6 }}
              className="mt-16 md:mt-32"
            >
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-sky-400 via-blue-500 to-blue-600">
                  The GovCon Data Advantage
                </h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Our AI-powered platform delivers measurable results across every stage of the contracting lifecycle
                </p>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {[
                  { value: '10x', label: 'Faster Proposals', description: 'AI-accelerated proposal generation' },
                  { value: 'Instant', label: 'Bid/No-Bid Decisions', description: 'AI-powered opportunity scoring' },
                  { value: '3x', label: 'Higher Win Rates', description: 'Data-driven competitive positioning' },
                  { value: '40hrs', label: 'Saved Per Bid', description: 'Automated research & compliance' },
                ].map((stat, idx) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 24, scale: 0.95 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: idx * 0.12, ease: 'easeOut' }}
                    className={cn(
                      "text-center p-6 md:p-8 rounded-2xl border transition-all duration-300",
                      "backdrop-blur-xl border-white/20 dark:border-white/10 bg-white/5 dark:bg-black/10",
                      "hover:shadow-[0_0_30px_-5px_rgba(14,165,233,0.25)] hover:-translate-y-1 hover:border-sky-500/30"
                    )}
                    style={{
                      boxShadow: '0 4px 16px 0 rgba(31, 38, 135, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.08) inset',
                    }}
                  >
                    <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-sky-400 to-blue-600 bg-clip-text text-transparent mb-2">
                      {stat.value}
                    </div>
                    <div className="text-sm font-semibold text-foreground mb-1">{stat.label}</div>
                    <div className="text-xs text-muted-foreground">{stat.description}</div>
                  </motion.div>
                ))}
              </div>
            </motion.section>

            {/* CTA */}
            <motion.section
              initial={{ opacity: 0, y: isMobile ? 20 : 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: isMobile ? 0.3 : 0.5, delay: isMobile ? 0 : 0.2 }}
              className={cn(
                "text-center mt-16 md:mt-24 mb-16 rounded-3xl p-8 md:p-12 border relative",
                isMobile 
                  ? "bg-slate-900/95 border-blue-500/30" 
                  : "backdrop-blur-xl border-white/20 dark:border-white/10 bg-white/5 dark:bg-black/10"
              )}
              style={isMobile ? {
                boxShadow: '0 4px 20px rgba(59, 130, 246, 0.15)',
              } : {
                boxShadow: `
                  0 8px 32px 0 rgba(31, 38, 135, 0.1),
                  0 0 0 1px rgba(255, 255, 255, 0.1) inset,
                  0 0 60px rgba(59, 130, 246, 0.1)
                `,
              }}
            >
              {!isMobile && (
                <div 
                  className="absolute inset-0 rounded-3xl border-2 border-blue-400/30 dark:border-blue-500/20"
                  style={{
                    animation: 'hero-glow-pulse 3s ease-in-out infinite',
                    boxShadow: '0 0 40px rgba(59, 130, 246, 0.2)',
                  }}
                />
              )}
              <div className="relative z-10">
                <h2 className="text-2xl md:text-3xl font-semibold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-sky-400 to-blue-600">
                  Boost Your Winning Potential
                </h2>
                <p className={cn(
                  "text-base md:text-lg mb-8 max-w-2xl mx-auto",
                  isMobile ? "text-gray-200" : "text-white dark:text-white"
                )} style={!isMobile ? {
                  textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
                } : undefined}>
                  Join the next generation of government contractors leveraging AI to win more contracts
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild size="lg" variant="glow" className="text-base md:text-lg px-6 md:px-8 h-11 md:h-12 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 shadow-lg shadow-blue-500/30">
                    <Link href="/signup">
                      Start 14-Day Free Trial
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="text-base md:text-lg px-6 md:px-8 h-11 md:h-12 backdrop-blur-sm border-white/30 dark:border-white/20 text-foreground hover:bg-white/10 dark:hover:bg-white/5">
                    <Link href="/pricing">
                      View Pricing
                    </Link>
                  </Button>
                </div>
              </div>
            </motion.section>
          </div>
        </motion.div>
      )}
    </>
  )
}